import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { toAbsoluteUrl } from '@/lib/url';
import { rescuedAnimalsApi } from '../api';
import { useLookupsSnapshot, toOptionLabel } from '@/features/lookups/snapshot';
import { Loader2, Save, ArrowLeft } from 'lucide-react';
import PhotosInput, { PhotoDraft } from '@/features/animals/form/PhotosInput';
import { useAuth } from '@/hooks/useAuth';
import { sheltersApi } from '@/features/shelters/api';
import { FOUND_CONTEXT_OPTIONS, VISIBILITY_DAYS_OPTIONS } from '../types';

const MAX_PHOTOS = 3;

const rescuedAnimalSchema = z.object({
  shelterId: z.number({ required_error: 'La entidad es obligatoria' }).int().positive(),
  intakeDate: z.string().min(1, 'La fecha de ingreso es obligatoria'),
  speciesId: z.number({ required_error: 'La especie es obligatoria' }).int().positive(),
  sexId: z.number().int().positive().optional(),
  sizeId: z.number().int().positive().optional(),
  conditionId: z.number({ required_error: 'La condición es obligatoria' }).int().positive(),
  foundArea: z.string().min(1, 'El sector/barrio es obligatorio').max(200),
  foundContext: z.enum(['report', 'found_on_street', 'brought_by_rescuer'], {
    required_error: 'El contexto del hallazgo es obligatorio',
  }),
  description: z.string().min(1, 'La descripción es obligatoria').max(800),
  visibilityDays: z.number().refine((v) => [15, 30, 60, 90].includes(v), 'Selecciona un plazo válido'),
});

type RescuedAnimalFormData = z.infer<typeof rescuedAnimalSchema>;

export default function RescuedAnimalFormPage() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { shelterId: authShelterId, isAdminWithShelter } = useAuth();

  const { data: snapshot, isLoading: isLookupsLoading } = useLookupsSnapshot();

  const { data: sheltersData } = useQuery({
    queryKey: ['shelters-for-rescued-animal-form'],
    queryFn: () => sheltersApi.list({ pageSize: 200 }),
    enabled: !isAdminWithShelter,
  });

  const { data: rescuedAnimalData, isLoading: isLoadingRescuedAnimal } = useQuery({
    queryKey: ['rescued-animal', id],
    queryFn: () => rescuedAnimalsApi.getById(Number(id)),
    enabled: isEdit,
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RescuedAnimalFormData>({
    resolver: zodResolver(rescuedAnimalSchema),
    defaultValues: {
      shelterId: isAdminWithShelter ? authShelterId : undefined,
      intakeDate: new Date().toISOString().slice(0, 10),
      visibilityDays: 30,
    },
  });

  const [photos, setPhotos] = useState<PhotoDraft[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (rescuedAnimalData) {
      const r = rescuedAnimalData;
      reset({
        shelterId: r.shelterId,
        intakeDate: String(r.intakeDate).slice(0, 10),
        speciesId: r.speciesId,
        sexId: r.sexId ?? undefined,
        sizeId: r.sizeId ?? undefined,
        conditionId: r.conditionId,
        foundArea: r.foundArea,
        foundContext: r.foundContext,
        description: r.description,
        visibilityDays: r.visibilityDays,
      });
      setExistingPhotos(r.photos ?? []);
    }
  }, [rescuedAnimalData, reset]);

  const onSubmit = async (data: RescuedAnimalFormData) => {
    setIsSaving(true);
    try {
      if (isEdit) {
        const uploadedPhotoUrls = existingPhotos.map((p) => ({ url: p.url, isPrimary: !!p.isPrimary }));
        await rescuedAnimalsApi.updateFull(Number(id), data, uploadedPhotoUrls.length ? uploadedPhotoUrls : undefined);
        if (photos.length) {
          await rescuedAnimalsApi.uploadPhotos(Number(id), photos.map((p) => p.file));
        }
        toast({ title: 'Actualizado', description: 'El registro se actualizó correctamente.' });
      } else {
        const created = await rescuedAnimalsApi.createFull(data);
        const newId = created?.id;
        if (newId && photos.length) {
          await rescuedAnimalsApi.uploadPhotos(Number(newId), photos.map((p) => p.file));
        }
        toast({ title: 'Creado', description: 'El animal encontrado se registró correctamente.' });
      }
      queryClient.invalidateQueries({ queryKey: ['rescued-animals'] });
      navigate('/rescued-animals');
    } catch (e: any) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error?.message || 'No se pudo guardar el registro.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isLoading = isLookupsLoading || (isEdit && isLoadingRescuedAnimal);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <Button type="button" variant="ghost" size="icon" onClick={() => navigate('/rescued-animals')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-semibold">{isEdit ? 'Editar' : 'Nuevo'} animal encontrado</h1>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Card className="p-6 space-y-4">
              {!isAdminWithShelter && (
                <div className="space-y-2">
                  <Label>Entidad</Label>
                  <Controller
                    control={control}
                    name="shelterId"
                    render={({ field }) => (
                      <Select value={field.value ? String(field.value) : ''} onValueChange={(v) => field.onChange(Number(v))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona una entidad" />
                        </SelectTrigger>
                        <SelectContent>
                          {(sheltersData?.data ?? sheltersData ?? []).map((s: any) => (
                            <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.shelterId && <p className="text-sm text-destructive">{errors.shelterId.message}</p>}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="intakeDate">Fecha de ingreso</Label>
                  <Input id="intakeDate" type="date" {...register('intakeDate')} />
                  {errors.intakeDate && <p className="text-sm text-destructive">{errors.intakeDate.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label>Plazo de visibilidad</Label>
                  <Controller
                    control={control}
                    name="visibilityDays"
                    render={({ field }) => (
                      <Select value={String(field.value)} onValueChange={(v) => field.onChange(Number(v))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VISIBILITY_DAYS_OPTIONS.map((d) => (
                            <SelectItem key={d} value={String(d)}>{d} días</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Especie</Label>
                  <Controller
                    control={control}
                    name="speciesId"
                    render={({ field }) => (
                      <Select value={field.value ? String(field.value) : ''} onValueChange={(v) => field.onChange(Number(v))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona" />
                        </SelectTrigger>
                        <SelectContent>
                          {(snapshot?.species ?? []).map((s: any) => (
                            <SelectItem key={s.id} value={String(s.id)}>{toOptionLabel(s)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.speciesId && <p className="text-sm text-destructive">{errors.speciesId.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label>Sexo (opcional)</Label>
                  <Controller
                    control={control}
                    name="sexId"
                    render={({ field }) => (
                      <Select value={field.value ? String(field.value) : ''} onValueChange={(v) => field.onChange(v ? Number(v) : undefined)}>
                        <SelectTrigger>
                          <SelectValue placeholder="No determinado" />
                        </SelectTrigger>
                        <SelectContent>
                          {(snapshot?.sexes ?? []).map((s: any) => (
                            <SelectItem key={s.id} value={String(s.id)}>{toOptionLabel(s)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tamaño aproximado (opcional)</Label>
                  <Controller
                    control={control}
                    name="sizeId"
                    render={({ field }) => (
                      <Select value={field.value ? String(field.value) : ''} onValueChange={(v) => field.onChange(v ? Number(v) : undefined)}>
                        <SelectTrigger>
                          <SelectValue placeholder="No determinado" />
                        </SelectTrigger>
                        <SelectContent>
                          {(snapshot?.sizes ?? []).map((s: any) => (
                            <SelectItem key={s.id} value={String(s.id)}>{toOptionLabel(s)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Condición al ingreso</Label>
                  <Controller
                    control={control}
                    name="conditionId"
                    render={({ field }) => (
                      <Select value={field.value ? String(field.value) : ''} onValueChange={(v) => field.onChange(Number(v))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona" />
                        </SelectTrigger>
                        <SelectContent>
                          {(snapshot?.rescuedAnimalConditions ?? []).map((c: any) => (
                            <SelectItem key={c.id} value={String(c.id)}>{toOptionLabel(c)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.conditionId && <p className="text-sm text-destructive">{errors.conditionId.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label>Contexto del hallazgo</Label>
                  <Controller
                    control={control}
                    name="foundContext"
                    render={({ field }) => (
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona" />
                        </SelectTrigger>
                        <SelectContent>
                          {FOUND_CONTEXT_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.foundContext && <p className="text-sm text-destructive">{errors.foundContext.message}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="foundArea">Sector / barrio donde fue encontrado</Label>
                <Input id="foundArea" {...register('foundArea')} placeholder="Ej: Barrio Centro" />
                {errors.foundArea && <p className="text-sm text-destructive">{errors.foundArea.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descripción (rasgos identificativos)</Label>
                <Textarea id="description" {...register('description')} rows={4} placeholder="Ej: Perro mediano, collar rojo, mancha blanca en el pecho, cojea de la pata trasera derecha" />
                {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
              </div>
            </Card>

            <Card className="p-6 space-y-4">
              <div>
                <Label>Fotos ({existingPhotos.length + photos.length}/{MAX_PHOTOS})</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Máximo {MAX_PHOTOS} fotos, enfocadas en rasgos identificativos (rostro, marcas, collar). Evita fotos de heridas explícitas.
                </p>
              </div>

              {existingPhotos.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {existingPhotos.map((p) => (
                    <img key={p.id} src={toAbsoluteUrl(p.url)} alt="" className="w-full h-24 object-cover rounded" />
                  ))}
                </div>
              )}

              <PhotosInput
                value={photos}
                onChange={setPhotos}
                maxPhotos={Math.max(0, MAX_PHOTOS - existingPhotos.length)}
                helpText={`Formatos: JPG, PNG, WEBP. Máximo ${MAX_PHOTOS} fotos en total.`}
              />
            </Card>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate('/rescued-animals')}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Guardar
              </Button>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
}
